import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2,
  Code2,
  Shield,
  Zap,
  Brain,
  Clock,
  AlertTriangle,
  Target,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Award,
  Users,
  FileText,
  BarChart3,
  Rocket,
  Zap as Lightning,
  Code,
  Database,
  GitBranch,
  Cpu,
  DollarSign,
  Globe,
  Search,
  Scale,
  Lightbulb,
  Briefcase,
  Settings,
  PieChart,
  Gauge,
  Wrench,
  Sparkles,
  Calendar,
  Star,
  Cloud as CloudIcon
} from 'lucide-react';
import '../../styles/ServicePageCommon.css';
import './SpecialEngagements.css';

const SpecialEngagements = () => {
  const location = useLocation();

  return (
    <div className="special-engagements-service-page">
      {/* Main Content */}
      <section className="special-engagements-content-section">
        <div className="special-engagements-header">
          <h1 className="special-engagements-title golden-gradient">Special Engagements</h1>
          <p className="special-engagements-subtitle">
            When you need rapid, high-impact solutions that deliver measurable results, our proven approach combines strategic thinking with tactical execution.
          </p>
        </div>

        <div className="special-engagements-content-layout">
          {/* Main Content Area */}
          <div className="special-engagements-main-content">
            <div className="special-engagements-overview">
              <h2>Why engage Cospin for Special Projects?</h2>
              <p className="special-engagements-overview-subtitle">
              </p>
              <div className="special-engagements-why-grid">
                <div className="special-engagements-why-card">
                  <div className="special-engagements-why-header">
                    <h4>Your Need</h4>
                    <h4>Our Excellence</h4>
                  </div>
                  <div className="special-engagements-why-content">
                    <div className="special-engagements-why-item">
                      <div className="special-engagements-why-need">
                        <Target size={20} strokeWidth={1.5} />
                        <span>Strategic clarity</span>
                      </div>
                      <div className="special-engagements-why-deliver">
                        We bring a founder's mindset and a consultant's toolkit
                      </div>
                    </div>
                    <div className="special-engagements-why-item">
                      <div className="special-engagements-why-need">
                        <Code size={20} strokeWidth={1.5} />
                        <span>Technical depth</span>
                      </div>
                      <div className="special-engagements-why-deliver">
                        Full-stack, cloud-native, and platform-aware execution
                      </div>
                    </div>
                    <div className="special-engagements-why-item">
                      <div className="special-engagements-why-need">
                        <DollarSign size={20} strokeWidth={1.5} />
                        <span>Business fluency</span>
                      </div>
                      <div className="special-engagements-why-deliver">
                        We speak finance, product, and engineering—fluently
                      </div>
                    </div>
                    <div className="special-engagements-why-item">
                      <div className="special-engagements-why-need">
                        <Rocket size={20} strokeWidth={1.5} />
                        <span>Speed + Substance</span>
                      </div>
                      <div className="special-engagements-why-deliver">
                        Quick wins that don't compromise long-term architecture
                      </div>
                    </div>
                    <div className="special-engagements-why-item">
                      <div className="special-engagements-why-need">
                        <Users size={20} strokeWidth={1.5} />
                        <span>Cultural alignment</span>
                      </div>
                      <div className="special-engagements-why-deliver">
                        We embed with your teams, not just advise from the sidelines
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="special-engagements-content-section">
              <h2>Platform Cost - Connecting profitability with scale</h2>
              <p className="special-engagements-section-tagline">"Do more with less—without compromise. Cospin helps you optimize cloud spend, rationalize tooling, and streamline IT operations to unlock hidden value."</p>
              <p className="special-engagements-section-highlight">
                Imagine your cost growing linearly with your volume, it will eat up your profit margin. This should also help in pricing your product or service accordingly.
              </p>

              <div className="special-engagements-optimization-grid">
                <div className="special-engagements-optimization-card">
                  <div className="special-engagements-optimization-header">
                    <div className="special-engagements-optimization-icon">
                      <CloudIcon size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Cloud Cost Optimization</h3>
                  </div>
                  <div className="special-engagements-optimization-what">
                    <h4>What we do:</h4>
                    <ul>
                      <li><CheckCircle size={16} strokeWidth={2} />Cloud cost audits and FinOps advisory</li>
                      <li><CheckCircle size={16} strokeWidth={2} />SaaS/tooling rationalization and license optimization</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Infra right-sizing and automation-led efficiency</li>
                      <li><CheckCircle size={16} strokeWidth={2} />IT process simplification and vendor consolidation</li>
                    </ul>
                  </div>
                  <div className="special-engagements-optimization-why">
                    <h4>Why Cospin:</h4>
                    <ul>
                      <li><Star size={16} strokeWidth={2} />Deep expertise in multi-cloud, DevOps, and platform engineering</li>
                      <li><Star size={16} strokeWidth={2} />Strategic lens on cost vs. capability trade-offs</li>
                      <li><Star size={16} strokeWidth={2} />Proven frameworks to reduce waste without disrupting delivery</li>
                    </ul>
                  </div>
                  <p className="special-engagements-optimization-summary">
                    We understand both the tech stack and the business stack, Cospin doesn't just cut costs—we optimize for long-term agility and resilience.
                  </p>
                </div>

                <div className="special-engagements-optimization-card">
                  <div className="special-engagements-optimization-header">
                    <div className="special-engagements-optimization-icon">
                      <PieChart size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Unit Economy & Platform Cost</h3>
                  </div>
                  <p className="special-engagements-optimization-description">
                    "Unit economics isn't just for startups. Cospin helps you decode your cost structure, align pricing with value, and build platforms that scale profitably."
                  </p>
                  <div className="special-engagements-optimization-what">
                    <h4>What we do:</h4>
                    <ul>
                      <li><CheckCircle size={16} strokeWidth={2} />Bringing SaaS financial elements into platform cost structure</li>
                      <li><CheckCircle size={16} strokeWidth={2} />CAC/LTV modeling and pricing diagnostics</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Platform cost attribution and margin analysis</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Cost-to-serve breakdowns across services or SKUs</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Strategic recommendations for monetization and scale</li>
                    </ul>
                  </div>
                  <div className="special-engagements-optimization-why">
                    <h4>Why Cospin:</h4>
                    <ul>
                      <li><Star size={16} strokeWidth={2} />Experience with SaaS org dynamics, equity trade-offs, and GTM alignment</li>
                      <li><Star size={16} strokeWidth={2} />Ability to bridge product, finance, and engineering conversations</li>
                      <li><Star size={16} strokeWidth={2} />Tailored dashboards and models for decision-makers</li>
                    </ul>
                  </div>
                  <p className="special-engagements-optimization-summary">
                    We don't just analyze—we translate numbers into narratives that drive action. With Cospin, your unit economics become a strategic weapon.
                  </p>
                </div>
              </div>
            </div>

            <div className="special-engagements-content-section">
              <h2>Quick wins – Event based</h2>
              <p className="special-engagements-section-tagline">"Sometimes, the fastest way to transform is to fix what's broken—together. Cospin curates high-energy events like fix-a-thons and hack-a-thons to drive quick wins, boost morale, and surface innovation from within."</p>

              <div className="special-engagements-events-grid">
                <div className="special-engagements-event-card">
                  <div className="special-engagements-event-header">
                    <div className="special-engagements-event-icon">
                      <Wrench size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Fix-a-thons</h3>
                  </div>
                  <p>Targeted sprints to reduce tech debt and improve stability</p>
                </div>
                <div className="special-engagements-event-card">
                  <div className="special-engagements-event-header">
                    <div className="special-engagements-event-icon">
                      <Sparkles size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Hack-a-thons</h3>
                  </div>
                  <p>Innovation bursts to prototype new ideas or features</p>
                </div>
                <div className="special-engagements-event-card">
                  <div className="special-engagements-event-header">
                    <div className="special-engagements-event-icon">
                      <Rocket size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Agile bootcamps</h3>
                  </div>
                  <p>Intensive training and retrospectives to reset delivery rhythm</p>
                </div>
                <div className="special-engagements-event-card">
                  <div className="special-engagements-event-header">
                    <div className="special-engagements-event-icon">
                      <Users size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Minimize Product & Engineering gaps</h3>
                  </div>
                  <p>Team alignment sessions to unlock blockers and improve collaboration</p>
                </div>
                <div className="special-engagements-event-card">
                  <div className="special-engagements-event-header">
                    <div className="special-engagements-event-icon">
                      <Users size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Eliminate GTM & Roadmap silos</h3>
                  </div>
                  <p>Conduct joint discovery workshops, user engagement sessions, surveys</p>
                </div>
              </div>

              <div className="special-engagements-events-why">
                <h4>Why Cospin:</h4>
                <ul>
                  <li><Star size={16} strokeWidth={2} />Proven playbooks for event design, facilitation, and follow-through</li>
                  <li><Star size={16} strokeWidth={2} />Ability to turn one-off events into sustained momentum</li>
                </ul>
                <p className="special-engagements-events-summary">
                  We don't just run events—we engineer outcomes. Cospin's special engagements are designed to energize teams, deliver value fast, and build belief in what's possible.
                </p>
              </div>
            </div>

            <div className="special-engagements-content-section">
              <h2>Agile, lean and development process optimization</h2>
              <p className="special-engagements-section-tagline">"Build better. Deliver faster. Adapt continuously."</p>

              <div className="special-engagements-process-grid">

                <div className="special-engagements-process-card">
                  <div className="special-engagements-process-header">
                    <div className="special-engagements-process-icon">
                      <Settings size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Process optimization</h3>
                  </div>
                  <p className="special-engagements-process-description">
                    "Your product process should be your growth engine—not your bottleneck. Cospin helps you streamline workflows, reduce waste, and accelerate value delivery."
                  </p>
                  <div className="special-engagements-process-what">
                    <h4>What we do:</h4>
                    <ul>
                      <li><CheckCircle size={16} strokeWidth={2} />Value stream mapping and delivery diagnostics</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Workflow redesign (from idea to deploy)</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Role clarity and cross-functional alignment</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Agile tooling and governance frameworks</li>
                    </ul>
                  </div>
                  <div className="special-engagements-process-why">
                    <h4>Why Cospin:</h4>
                    <ul>
                      <li><Star size={16} strokeWidth={2} />Strategic lens on org design, team topology, and delivery velocity</li>
                      <li><Star size={16} strokeWidth={2} />Cultural fluency to bridge global HQs and India delivery centers</li>
                      <li><Star size={16} strokeWidth={2} />Experience across startups and scaled SaaS orgs</li>
                      <li><Star size={16} strokeWidth={2} />Ability to align product, engineering, and business goals</li>
                    </ul>
                  </div>
                  <p className="special-engagements-process-summary">
                    We optimize for outcomes, not rituals. Cospin helps you ship faster—with clarity, confidence, and cohesion.
                  </p>
                </div>

                <div className="special-engagements-process-card">
                  <div className="special-engagements-process-header">
                    <div className="special-engagements-process-icon">
                      <Gauge size={28} strokeWidth={1.5} />
                    </div>
                    <h3>Lean principles & delivery excellence</h3>
                  </div>
                  <p className="special-engagements-process-description">
                    "Less waste. More flow. Better outcomes. Cospin applies lean thinking to help you build what matters—faster, smarter, and with fewer surprises."
                  </p>
                  <div className="special-engagements-process-what">
                    <h4>What we do:</h4>
                    <ul>
                      <li><CheckCircle size={16} strokeWidth={2} />Lean product development coaching (MVP, feedback loops, flow efficiency)</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Portfolio and capacity planning</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Continuous improvement frameworks (Kaizen, retrospectives, A3 thinking)</li>
                      <li><CheckCircle size={16} strokeWidth={2} />Metrics that matter: cycle time, throughput, flow load</li>
                    </ul>
                  </div>
                  <div className="special-engagements-process-why">
                    <h4>Why Cospin:</h4>
                    <ul>
                      <li><Star size={16} strokeWidth={2} />Founder-led expertise in lean, agile, and platform thinking</li>
                      <li><Star size={16} strokeWidth={2} />Proven ability to balance speed with compliance (e.g., healthtech, AI)</li>
                      <li><Star size={16} strokeWidth={2} />Systems thinking approach to org-wide agility</li>
                    </ul>
                  </div>
                  <p className="special-engagements-process-summary">
                    Cospin brings lean to life—not just in process, but in mindset. We help you build learning organizations that adapt and thrive.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

    </div>
  );
};

export default SpecialEngagements;
